/**
 * Comunicacoes (spec 041, 3.8): os e-mails do sistema, agrupados pelo assunto, e o editor de cada um (assunto, titulo,
 * paragrafos e o texto do botao), com as variaveis a mao e a previa montada pelo servico de e-mails a partir do
 * rascunho. So o admin salva e volta ao texto padrao; quem nao pode ve o texto e a previa. O painel fala com o backend,
 * nunca direto com o servico; sem o servico configurado, a frase do backend aparece.
 */
import {
  Aviso, Botao, CabecalhoDePagina, Carregando, Dialogo, EditorDeEmail, Grade, IconeVoltar, LinkInterno, Pilha, PreviaDeEmail, Secao, Selo, Tabela, Vazio, formatarDataHora, type ConteudoDeEmail, type RascunhoDeEmail,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { erroDe, useAcao, useApi, usePapel } from "../api";

type ItemDeTemplate = { chave: string; nome: string; descricao: string | null; assunto: string; variaveis: string[]; atualizadoEm: string; atualizadoPor: string | null; editado: boolean; tem_padrao: boolean; ativo?: boolean };
type Template = { chave: string; nome: string; descricao: string | null; assunto: string; conteudo: ConteudoDeEmail; variaveis: string[] };
type Previa = { assunto: string; html: string; texto: string };

/** Os grupos da lista, pela chave do template (so organizacao da tela; o que cada e-mail faz e do servico). */
const GRUPOS: { rotulo: string; descricao: string; casa: (chave: string) => boolean }[] = [
  { rotulo: "Acesso", descricao: "Criar senha, código de verificação, avisos de segurança da conta.", casa: (c) => /^(definir-senha|codigo-de-verificacao|acesso-|senha-|email-de-acesso|convite|sistema-novo)/.test(c) },
  { rotulo: "Compra", descricao: "O que o cliente recebe quando a compra é aprovada ou o plano é concedido.", casa: (c) => /^(compra-|pagamento-|plano-concedido|agendar-inicio)/.test(c) },
  { rotulo: "Mensalidade", descricao: "Lembretes antes do vencimento, a vencida e o comprovante recusado.", casa: (c) => /^(mensalidade-|comprovante-)/.test(c) },
  { rotulo: "Plano e etapas", descricao: "Plano liberado, suspenso, encerrado, avanço de etapa e prazo do teste.", casa: (c) => /^(matricula-|avanco-|etapa-|teste-)/.test(c) },
  { rotulo: "Avisos da equipe", descricao: "O que vai para os administradores: alertas e vendas.", casa: (c) => /^(alerta-|venda-)/.test(c) },
];
const OUTROS = { rotulo: "Outros", descricao: "Comunicados e mensagens avulsas." };
const grupoDe = (chave: string) => GRUPOS.find((g) => g.casa(chave)) ?? OUTROS;

function useTemplates() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["comunicacoes"], queryFn: () => ler<{ itens: ItemDeTemplate[] }>("/v1/admin/comunicacoes/templates") });
}

const seloDe = (t: ItemDeTemplate) => (t.editado ? <Selo tom="info">Editado</Selo> : <Selo>Texto padrão</Selo>);
/** Desligado (Vitor, 08/10): o sistema registra o pedido e nao manda. Sem o campo (servico antigo), conta como ligado. */
const seloDoEnvio = (t: ItemDeTemplate) => (t.ativo === false ? <Selo tom="alerta">Desligado</Selo> : <Selo tom="sucesso">Ligado</Selo>);

export function PaginaComunicacoes() {
  const q = useTemplates();
  const cabecalho = <CabecalhoDePagina titulo="E-mails" descricao="Os e-mails que o sistema manda sozinho. Ligue ou desligue cada um, e abra para mudar o assunto e o texto, com a prévia de como o cliente recebe. O texto editado não volta sozinho para o padrão." />;
  if (q.isLoading) return <Pilha espaco={5}>{cabecalho}<Carregando compacto texto="Buscando os e-mails" /></Pilha>;
  if (q.isError) return <Pilha espaco={5}>{cabecalho}<Aviso tipo="erro">{erroDe(q.error)}</Aviso></Pilha>;
  const itens = q.data?.itens ?? [];
  const grupos = [...GRUPOS, OUTROS].map((g) => ({ ...g, itens: itens.filter((t) => grupoDe(t.chave).rotulo === g.rotulo) })).filter((g) => g.itens.length);
  return (
    <Pilha espaco={5}>
      {cabecalho}
      {!grupos.length && <Vazio titulo="Nenhum e-mail cadastrado">O serviço de e-mails ainda não tem textos neste ambiente.</Vazio>}
      {grupos.map((g) => (
        <Secao key={g.rotulo} rotulo={`${g.itens.length} ${g.itens.length === 1 ? "e-mail" : "e-mails"}`} titulo={g.rotulo}>
          <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{g.descricao}</p>
          <Tabela
            linhas={g.itens}
            chave={(t) => t.chave}
            colunas={[
              // A descricao (quando sai, pra quem) fica na tela do e-mail: aqui, o nome e o assunto bastam pra achar.
              { chave: "email", rotulo: "E-mail", celula: (t) => <><LinkInterno href={`/comunicacoes/${t.chave}`}>{t.nome}</LinkInterno><span className="mtc-tabela__apoio" style={{ overflowWrap: "anywhere" }}>{t.assunto}</span></> },
              { chave: "envio", rotulo: "Envio", celula: (t) => seloDoEnvio(t) },
              { chave: "situacao", rotulo: "Texto", alinhar: "direita", celula: (t) => <>{seloDe(t)}{t.editado && <span className="mtc-tabela__apoio">em {formatarDataHora(t.atualizadoEm)}</span>}</> },
            ]}
          />
        </Secao>
      ))}
    </Pilha>
  );
}

/** O mesmo conteudo, sem paragrafo em branco: e o que vai pra previa e pro salvar. */
const limpo = (r: RascunhoDeEmail): RascunhoDeEmail => ({ assunto: r.assunto.trim(), conteudo: { titulo: r.conteudo.titulo.trim(), paragrafos: r.conteudo.paragrafos.filter((p) => p.trim()), ...(r.conteudo.cta ? { cta: { texto: r.conteudo.cta.texto.trim(), url_var: r.conteudo.cta.url_var } } : {}) } });
const completo = (r: RascunhoDeEmail) => Boolean(r.assunto && r.conteudo.titulo && r.conteudo.paragrafos.length && (!r.conteudo.cta || r.conteudo.cta.texto));
const mesmo = (a: RascunhoDeEmail, b: RascunhoDeEmail) => JSON.stringify(limpo(a)) === JSON.stringify(limpo(b));

/** O valor depois de `ms` sem mudar: a previa nao pede uma montagem a cada letra. */
function useCalmo<T>(valor: T, ms: number): T {
  const [calmo, setCalmo] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setCalmo(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return calmo;
}

export function PaginaComunicacao() {
  const { chave = "" } = useParams();
  const { ler, postar, por } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const lista = useTemplates();
  const t = useQuery({ queryKey: ["comunicacoes", chave], queryFn: () => ler<Template>(`/v1/admin/comunicacoes/templates/${chave}`) });
  const salvo: RascunhoDeEmail | null = t.data ? { assunto: t.data.assunto, conteudo: t.data.conteudo } : null;
  const [rascunho, setRascunho] = useState<RascunhoDeEmail | null>(null);
  // Chegou (ou mudou) o salvo: o rascunho recomeca dele.
  useEffect(() => { if (t.data) setRascunho({ assunto: t.data.assunto, conteudo: t.data.conteudo }); }, [t.data]);
  const calmo = useCalmo(rascunho, 450);
  const pronto = calmo ? limpo(calmo) : null;
  const valido = Boolean(pronto && completo(pronto));
  const previa = useQuery({
    queryKey: ["comunicacoes", chave, "previa", pronto && valido ? JSON.stringify(pronto) : "salvo"],
    queryFn: () => postar<Previa>(`/v1/admin/comunicacoes/templates/${chave}/previa`, pronto && valido ? { assunto: pronto.assunto, conteudo: pronto.conteudo } : {}),
    enabled: Boolean(t.data),
    placeholderData: (antes) => antes,
  });
  const salvar = useAcao();
  const restaurar = useAcao();
  const chaveDoEnvio = useAcao();
  const [feito, setFeito] = useState<string>();
  const [confirmar, setConfirmar] = useState(false);
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const admin = pode("ADMIN");
  const item = lista.data?.itens.find((x) => x.chave === chave);

  const voltar = <LinkInterno href="/comunicacoes" className="mtc-voltar"><IconeVoltar tamanho={16} />E-mails</LinkInterno>;
  if (t.isLoading || (!rascunho && t.data)) return <Pilha espaco={4}>{voltar}<Carregando compacto texto="Abrindo o e-mail" /></Pilha>;
  if (!t.data || !rascunho || !salvo) return <Pilha espaco={4}>{voltar}<Aviso tipo="erro">{t.error ? erroDe(t.error) : "E-mail não encontrado."}</Aviso></Pilha>;

  const mudou = !mesmo(rascunho, salvo);
  const podeSalvar = admin && mudou && completo(limpo(rascunho));
  const recarregar = () => qc.invalidateQueries({ queryKey: ["comunicacoes"] });
  const aoSalvar = async () => {
    setFeito(undefined);
    const ok = await salvar.rodar(() => por(`/v1/admin/comunicacoes/templates/${chave}`, limpo(rascunho)));
    if (ok) { await recarregar(); setFeito("Texto salvo. Os próximos e-mails já saem assim."); }
  };
  const ligado = item?.ativo !== false;
  const aoTrocarEnvio = async () => {
    setFeito(undefined);
    const ok = await chaveDoEnvio.rodar(() => por(`/v1/admin/comunicacoes/templates/${chave}/ativo`, { ativo: !ligado }));
    setConfirmarEnvio(false);
    if (ok) { await recarregar(); setFeito(ligado ? "E-mail desligado. Os próximos pedidos ficam registrados e não saem." : "E-mail ligado. Volta a sair a partir do próximo pedido."); }
  };
  const aoRestaurar = async () => {
    setFeito(undefined);
    const ok = await restaurar.rodar(() => postar(`/v1/admin/comunicacoes/templates/${chave}/restaurar`));
    setConfirmar(false);
    if (ok) { await recarregar(); setFeito("O e-mail voltou ao texto padrão."); }
  };

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina voltar={voltar} titulo={t.data.nome} descricao={t.data.descricao ?? undefined} acoes={item ? seloDe(item) : undefined} />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {(salvar.erro || restaurar.erro || chaveDoEnvio.erro) && <Aviso tipo="erro">{salvar.erro ?? restaurar.erro ?? chaveDoEnvio.erro}</Aviso>}
      {item && (
        <Aviso tipo={ligado ? "info" : "alerta"}>
          <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-3)", flexWrap: "wrap" }}>
            <span>{ligado ? "Este e-mail está ligado: sai sempre que o sistema pede." : "Este e-mail está desligado: o sistema registra o pedido e não manda. O aviso na área do cliente continua."}</span>
            {admin && <Botao tamanho="pequeno" variante={ligado ? "secundario" : "primario"} onClick={() => setConfirmarEnvio(true)}>{ligado ? "Desligar este e-mail" : "Ligar este e-mail"}</Botao>}
          </div>
        </Aviso>
      )}
      {!admin && <Aviso tipo="info">Mudar o texto dos e-mails é do admin. Você pode ler o texto e ver a prévia.</Aviso>}
      <Grade minimo={400}>
        <Secao rotulo="Texto" titulo="O que o cliente lê">
          <EditorDeEmail valor={rascunho} aoMudar={(v) => { setFeito(undefined); setRascunho(v); }} variaveis={t.data.variaveis} somenteLeitura={!admin} />
          {admin && (
            <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
              <Botao carregando={salvar.carregando} disabled={!podeSalvar} onClick={() => void aoSalvar()}>Salvar</Botao>
              {mudou && <Botao variante="fantasma" onClick={() => setRascunho(salvo)}>Descartar mudanças</Botao>}
              {item?.editado && item.tem_padrao && <Botao variante="secundario" onClick={() => setConfirmar(true)}>Voltar ao texto padrão</Botao>}
            </div>
          )}
          {admin && mudou && !completo(limpo(rascunho)) && <span className="mtc-campo__dica">Preencha o assunto, o título, pelo menos um parágrafo{rascunho.conteudo.cta ? " e o texto do botão" : ""} para salvar.</span>}
        </Secao>
        <div className="mtc-coluna-fixa">
        <Secao rotulo="Prévia" titulo="Como chega no e-mail">
          <PreviaDeEmail
            assunto={previa.data?.assunto}
            html={previa.data?.html}
            carregando={previa.isFetching}
            erro={previa.isError ? erroDe(previa.error) : undefined}
            rodape={<span className="mtc-campo__dica">{valido ? "As variáveis aparecem entre colchetes, como [primeiro_nome]: no envio, viram o dado do cliente." : "Complete o rascunho para a prévia acompanhar."}{mudou ? " A prévia é do rascunho, ainda não salvo." : ""}</span>}
          />
        </Secao>
        </div>
      </Grade>
      <Dialogo
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        titulo="Voltar ao texto padrão?"
        descricao="O texto editado deste e-mail se perde e o padrão do sistema volta a valer, inclusive nas próximas atualizações dele."
        rodape={<><Botao variante="fantasma" onClick={() => setConfirmar(false)}>Manter o editado</Botao><Botao variante="perigo" carregando={restaurar.carregando} onClick={() => void aoRestaurar()}>Voltar ao padrão</Botao></>}
      />
      <Dialogo
        aberto={confirmarEnvio}
        aoFechar={() => setConfirmarEnvio(false)}
        titulo={ligado ? "Desligar este e-mail?" : "Ligar este e-mail?"}
        descricao={ligado ? "A partir de agora, quando o sistema pedir este e-mail, ele fica registrado e não sai. Dá pra ligar de novo a qualquer momento." : "A partir do próximo pedido, este e-mail volta a sair. Os que foram pedidos enquanto estava desligado não saem."}
        rodape={<><Botao variante="fantasma" onClick={() => setConfirmarEnvio(false)}>Cancelar</Botao><Botao variante={ligado ? "perigo" : "primario"} carregando={chaveDoEnvio.carregando} onClick={() => void aoTrocarEnvio()}>{ligado ? "Desligar" : "Ligar"}</Botao></>}
      />
    </Pilha>
  );
}
